/**
 * Modal dialog for setting a first-team player's manual annual salary
 * (SE-8409).
 *
 * Same shell as `PromoteActionModal` — title, € input (Millions/Thousands
 * toggle via `MoneyInput variant="expanded"`), Cancel/Confirm — minus the
 * Contract Length field and the Live Impact panel. Those two are Promote-only:
 * duration only exists on a fresh signing, and Live Impact projects a
 * scenario draft, which this write bypasses entirely (it saves directly).
 * @module features/squad-planning/components/AddAnnualSalaryModal
 */

import React, { memo } from "react";

import MoneyInput from "@/components/molecules/MoneyInput";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Heading } from "@/components/atoms/Heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";

/** Props for {@link AddAnnualSalaryModalView} */
export interface AddAnnualSalaryModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Player name for the title */
  readonly playerName: string;
  /** Current annual salary draft value */
  readonly annualSalary: number | null;
  /** Called when the salary input changes */
  readonly onAnnualSalaryChange: (value: number | null) => void;
  /** Called to save the salary */
  readonly onConfirm: () => void;
  /** Called to close the modal without saving */
  readonly onCancel: () => void;
  /** Whether the save is in flight (disables Confirm, keeps the modal open) */
  readonly isSaving: boolean;
}

/**
 * Renders the Add Annual Salary modal.
 * @param props - Component props
 * @param props.isOpen - Whether the modal is visible
 * @param props.playerName - Player name for the title
 * @param props.annualSalary - Current salary draft value
 * @param props.onAnnualSalaryChange - Salary change handler
 * @param props.onConfirm - Save handler
 * @param props.onCancel - Close handler
 * @param props.isSaving - Whether the save is in flight
 */
const AddAnnualSalaryModalView = ({
  isOpen,
  playerName,
  annualSalary,
  onAnnualSalaryChange,
  onConfirm,
  onCancel,
  isSaving,
}: AddAnnualSalaryModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onCancel} size="md" avoidKeyboard>
    <ModalBackdrop />
    <ModalContent className="p-6">
      <ModalHeader>
        <Heading variant="title-lg">
          Annual Salary{" "}
          <Text variant="title" className="text-accent-primary">
            {playerName}
          </Text>
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Annual Salary
            </Text>
            <MoneyInput
              value={annualSalary}
              onConfirm={onAnnualSalaryChange}
              onChange={onAnnualSalaryChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction="horizontal"
          className="flex-1 flex-wrap items-center justify-end gap-4"
        >
          <Button
            variant="outline"
            onPress={onCancel}
            size="md"
            className="border-outline-strong"
            label="Cancel"
            labelTone="primary"
          />
          <Button
            onPress={onConfirm}
            size="md"
            className="bg-accent-primary"
            isDisabled={isSaving || annualSalary == null || annualSalary <= 0}
            label="Confirm"
            labelTone="on-accent"
          />
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

AddAnnualSalaryModalView.displayName = "AddAnnualSalaryModalView";

export default memo(AddAnnualSalaryModalView);
